import {computed, ref} from 'vue';
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {auth, db} from '@/firebase';

const user = ref(null);
const authReady = ref(false);
const username = ref(null);

onAuthStateChanged(auth, async (firebaseUser) => {
  user.value = firebaseUser;
  authReady.value = true;
  if (firebaseUser) {
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
    username.value = snap.exists() ? (snap.data().username ?? null) : null;
  } else {
    username.value = null;
  }
});

const MAGIC_LINK_EMAIL_KEY = 'tarski-magic-email';

export function useAuth() {
  const isLoggedIn = computed(() => !!user.value);

  async function loginWithGoogle() {
    await signInWithPopup(auth, new GoogleAuthProvider());
  }

  async function loginWithMicrosoft() {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({prompt: 'select_account'});
    await signInWithPopup(auth, provider);
  }

  async function sendMagicLink(email) {
    const actionCodeSettings = {
      url: window.location.origin + window.location.pathname,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
  }

  async function completeMagicLinkSignIn() {
    if (!isSignInWithEmailLink(auth, window.location.href)) return false;
    let email = localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
    if (!email) {
      email = window.prompt('Please confirm your email to complete sign-in:');
    }
    await signInWithEmailLink(auth, email, window.location.href);
    localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
    // Strip the sign-in query params from the URL
    window.history.replaceState(null, '', window.location.pathname);
    return true;
  }

  async function logout() {
    await signOut(auth);
  }

  async function saveRun(runData) {
    if (!user.value) return null;
    return addDoc(collection(db, 'runs'), {
      ...runData,
      uid: user.value.uid,
      shared: false,
      completedAt: serverTimestamp(),
    });
  }

  async function fetchRun(id) {
    const snap = await getDoc(doc(db, 'runs', id));
    if (!snap.exists()) return null;
    return {id: snap.id, ...snap.data()};
  }

  async function fetchRuns() {
    if (!user.value) return [];
    const q = query(
        collection(db, 'runs'),
        where('uid', '==', user.value.uid),
        orderBy('completedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(snap => ({id: snap.id, ...snap.data()}));
  }

  async function publishRun(run) {
    if (!user.value) return;
    await setDoc(doc(db, 'leaderboard', run.id), {
      uid:              user.value.uid,
      runId:            run.id,
      publishedAt:      serverTimestamp(),
      completedAt:      run.completedAt,
      complexity:       run.complexity,
      challengeAmount:  run.challengeAmount,
      score:            run.score,
      correctPct:       run.correctPct ?? null,
      timePerChallenge: run.timePerChallenge,
    });
  }

  async function unpublishRun(runId) {
    if (!user.value) return;
    await deleteDoc(doc(db, 'leaderboard', runId));
  }

  async function shareDareRun(runId) {
    if (!user.value) return;
    await updateDoc(doc(db, 'runs', runId), {
      shared: true,
      displayName: username.value || user.value.displayName || null,
    });
  }

  async function recordDareResult(runId, score, correctPct) {
    await updateDoc(doc(db, 'runs', runId), {
      challengerScore: score,
      challengerCorrectPct: correctPct,
      challengerCompletedAt: serverTimestamp(),
    });
  }

  async function fetchSharedRun(runId) {
    const snap = await getDoc(doc(db, 'runs', runId));
    if (!snap.exists() || !snap.data().shared) return null;
    return { id: snap.id, ...snap.data() };
  }

  async function fetchPublishedRunIds() {
    if (!user.value) return new Set();
    const q = query(collection(db, 'leaderboard'), where('uid', '==', user.value.uid));
    const snap = await getDocs(q);
    return new Set(snap.docs.map(d => d.id));
  }

  async function fetchLeaderboard() {
    const q = query(collection(db, 'leaderboard'), orderBy('score', 'desc'), limit(100));
    const snap = await getDocs(q);
    const entries = snap.docs.map(d => ({id: d.id, ...d.data()}));
    const uids = [...new Set(entries.map(e => e.uid))];
    const userSnaps = await Promise.all(uids.map(uid => getDoc(doc(db, 'users', uid))));
    const usernameMap = Object.fromEntries(
      userSnaps.filter(s => s.exists()).map(s => [s.id, s.data().username ?? null])
    );
    return entries.map(e => ({...e, username: usernameMap[e.uid] ?? null}));
  }

  async function isUsernameAvailable(name) {
    const snap = await getDoc(doc(db, 'usernames', name.toLowerCase()));
    return !snap.exists();
  }

  async function setUsername(name) {
    if (!user.value) return;
    const normalized = name.toLowerCase();
    const userRef = doc(db, 'users', user.value.uid);
    const usernameRef = doc(db, 'usernames', normalized);

    await runTransaction(db, async (tx) => {
      const existing = await tx.get(usernameRef);
      if (existing.exists() && existing.data().uid !== user.value.uid) {
        throw new Error('Username already taken');
      }
      // Release old username if there was one
      if (username.value && username.value.toLowerCase() !== normalized) {
        tx.delete(doc(db, 'usernames', username.value.toLowerCase()));
      }
      tx.set(usernameRef, {uid: user.value.uid});
      tx.set(userRef, {username: name}, {merge: true});
    });

    username.value = name;
  }

  return {
    user,
    isLoggedIn,
    authReady,
    username,
    loginWithGoogle,
    loginWithMicrosoft,
    sendMagicLink,
    completeMagicLinkSignIn,
    logout,
    saveRun,
    fetchRun,
    fetchRuns,
    isUsernameAvailable,
    setUsername,
    publishRun,
    unpublishRun,
    fetchPublishedRunIds,
    fetchLeaderboard,
    shareDareRun,
    fetchSharedRun,
    recordDareResult,
  };
}
